import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { Subscription } from 'rxjs';

import { IFilterContracts } from 'src/app/models/filters';
import { ApiVerificationService } from 'src/app/services/api/api-verification.service';

type ContractBatchMode = 'all' | 'withBatch' | 'withoutBatch';
type RateMode = 'all' | 'zeroRate' | 'nonZeroRate';

@Component({
  selector: 'app-esteira-contract-filter',
  templateUrl: './esteira-contract-filter.component.html',
  styleUrls: ['./esteira-contract-filter.component.scss']
})
export class EsteiraContractFilterComponent implements OnInit, OnChanges, OnDestroy {
  @Input() count = 0;
  @Input() totalAmount = 0;
  @Input() searchText = '';

  @Output() filtersSubmitted = new EventEmitter<{
    searchText: string;
    filters: IFilterContracts;
  }>();

  filterForm = this._fb.nonNullable.group({
    searchText: '',
    context: 'invoice',
    stages: [[] as string[]],
    statuses: [[] as string[]],
    transferStatuses: [[] as string[]],
    orderStatuses: [[] as string[]],
    contractMode: 'all' as ContractBatchMode,
    transferContractMode: 'all' as ContractBatchMode,
    rateMode: 'all' as RateMode,
    transferRateMode: 'all' as RateMode
  });

  stageOptions: string[] = [];
  statusOptions: { label: string; value: string }[] = [];
  transferStatusOptions: string[] = [];
  orderStatusOptions: string[] = [];
  openDropdown:
    | 'stages'
    | 'statuses'
    | 'transferStatuses'
    | 'orderStatuses'
    | null = null;
  isFiltersExpanded = true;

  readonly contractModes: ReadonlyArray<{ label: string; value: ContractBatchMode }> = [
    { label: 'Todos', value: 'all' },
    { label: 'Com lote', value: 'withBatch' },
    { label: 'Sem lotes', value: 'withoutBatch' }
  ];

  readonly rateModes: ReadonlyArray<{ label: string; value: RateMode }> = [
    { label: 'Todas', value: 'all' },
    { label: 'Zeradas', value: 'zeroRate' },
    { label: 'Não zeradas', value: 'nonZeroRate' }
  ];

  private readonly statusRemovedFilter: string[] = [
    'aguardando nota fiscal',
    'aprovacao do lote',
    'erro',
    'solicitado',
    'liberado'
  ];

  private readonly transferStatusRemovedFilter: string[] = [
    'liberado parcialmente',
    'liberado manual'
  ];

  private subs: Subscription[] = [];

  constructor(
    private _fb: FormBuilder,
    private _verificationService: ApiVerificationService
  ) {}

  ngOnInit(): void {
    this.filterForm.controls.searchText.setValue(this.searchText ?? '');
    this.loadFilterOptions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['searchText'] && !changes['searchText'].firstChange) {
      this.filterForm.controls.searchText.setValue(this.searchText ?? '', {
        emitEvent: false
      });
    }
  }

  ngOnDestroy(): void {
    this.subs.forEach((sub) => sub.unsubscribe());
  }

  toggleDropdown(
    event: Event,
    dropdown: 'stages' | 'statuses' | 'transferStatuses' | 'orderStatuses',
    context: 'invoice' | 'transfer'
  ): void {
    event.stopPropagation();
    this.setContext(context, false);
    this.openDropdown = this.openDropdown === dropdown ? null : dropdown;
  }

  closeDropdown(): void {
    this.openDropdown = null;
  }

  toggleFiltersVisibility(): void {
    this.isFiltersExpanded = !this.isFiltersExpanded;

    if (!this.isFiltersExpanded) {
      this.closeDropdown();
    }
  }

  isStageSelected(label: string): boolean {
    return this.filterForm.controls.stages.value.includes(label);
  }

  isStatusSelected(label: string): boolean {
    return this.filterForm.controls.statuses.value.includes(label);
  }

  isTransferStatusSelected(label: string): boolean {
    return this.filterForm.controls.transferStatuses.value.includes(label);
  }

  isOrderStatusSelected(label: string): boolean {
    return this.filterForm.controls.orderStatuses.value.includes(label);
  }

  toggleStage(label: string, checked: boolean): void {
    const current = this.filterForm.controls.stages.value;
    this.filterForm.controls.stages.setValue(
      checked ? [...current, label] : current.filter((item) => item !== label)
    );
  }

  toggleStatus(label: string, checked: boolean): void {
    const current = this.filterForm.controls.statuses.value;
    this.filterForm.controls.statuses.setValue(
      checked ? [...current, label] : current.filter((item) => item !== label)
    );
  }

  toggleTransferStatus(label: string, checked: boolean): void {
    const current = this.filterForm.controls.transferStatuses.value;
    this.filterForm.controls.transferStatuses.setValue(
      checked ? [...current, label] : current.filter((item) => item !== label)
    );
  }

  toggleOrderStatus(label: string, checked: boolean): void {
    const current = this.filterForm.controls.orderStatuses.value;
    this.filterForm.controls.orderStatuses.setValue(
      checked ? [...current, label] : current.filter((item) => item !== label)
    );
  }

  getStageTriggerText(): string {
    return this.getTriggerText(
      this.filterForm.controls.stages.value,
      'Selecionar fases'
    );
  }

  getStatusTriggerText(): string {
    return this.getTriggerText(
      this.filterForm.controls.statuses.value,
      'Selecionar status'
    );
  }

  getTransferStatusTriggerText(): string {
    return this.getTriggerText(
      this.filterForm.controls.transferStatuses.value,
      'Selecionar status'
    );
  }

  getOrderStatusTriggerText(): string {
    return this.getTriggerText(
      this.filterForm.controls.orderStatuses.value,
      'Selecionar status OMIE'
    );
  }

  setContext(context: 'invoice' | 'transfer', closeDropdown = true): void {
    const contextChanged = this.filterForm.controls.context.value !== context;
    this.filterForm.controls.context.setValue(context);

    if (closeDropdown && contextChanged) {
      this.closeDropdown();
    }
  }

  setContractMode(mode: ContractBatchMode): void {
    this.setContext('invoice', false);
    this.filterForm.controls.contractMode.setValue(mode);
  }

  setTransferContractMode(mode: ContractBatchMode): void {
    this.setContext('transfer', false);
    this.filterForm.controls.transferContractMode.setValue(mode);
  }

  setRateMode(mode: RateMode): void {
    this.setContext('invoice', false);
    this.filterForm.controls.rateMode.setValue(mode);
  }

  setTransferRateMode(mode: RateMode): void {
    this.setContext('transfer', false);
    this.filterForm.controls.transferRateMode.setValue(mode);
  }

  isInvoiceContext(): boolean {
    return this.filterForm.controls.context.value === 'invoice';
  }

  clearFilters(): void {
    this.filterForm.setValue({
      searchText: '',
      context: this.filterForm.controls.context.value,
      stages: [],
      statuses: [],
      transferStatuses: [],
      orderStatuses: [],
      contractMode: 'all',
      transferContractMode: 'all',
      rateMode: 'all',
      transferRateMode: 'all'
    });
    this.closeDropdown();

    const filters = this.getDefaultFilters();
    localStorage.setItem('bag_filter', JSON.stringify(filters));
    this.filtersSubmitted.emit({ searchText: '', filters });
  }

  submitFilters(event?: Event): void {
    event?.preventDefault();
    this.closeDropdown();

    const selectedStatusLabels = this.filterForm.controls.statuses.value;
    const isInvoiceContext = this.isInvoiceContext();
    const contractBatchMode = isInvoiceContext
      ? this.filterForm.controls.contractMode.value
      : this.filterForm.controls.transferContractMode.value;
    const selectedRateMode = isInvoiceContext
      ? this.filterForm.controls.rateMode.value
      : this.filterForm.controls.transferRateMode.value;
    const selectedInvoiceBatchStatuses = this.statusOptions
      .filter((status) => selectedStatusLabels.includes(status.label))
      .map((status) => status.value.toLowerCase());

    const filters: IFilterContracts = {
      ...this.getDefaultFilters(),
      Context: isInvoiceContext ? 'invoice' : 'transfer',
      Stages: isInvoiceContext
        ? this.filterForm.controls.stages.value.map((stage) => stage.toLowerCase())
        : [],
      Status: [],
      BatchStatuses: isInvoiceContext
        ? selectedInvoiceBatchStatuses
        : [],
      StatusOfOnlending: isInvoiceContext
        ? []
        : this.filterForm.controls.transferStatuses.value.map((status) =>
            status.toLowerCase()
          ),
      OrderStatus: isInvoiceContext
        ? []
        : this.filterForm.controls.orderStatuses.value.map((status) =>
            status.toLowerCase()
          ),
      IsZeroRate: isInvoiceContext && selectedRateMode === 'zeroRate',
      WithoutBatch: contractBatchMode === 'withoutBatch',
      ContractBatchMode: contractBatchMode,
      RateMode: isInvoiceContext ? selectedRateMode : 'all',
      TransferRateMode: isInvoiceContext ? 'all' : selectedRateMode
    };

    localStorage.setItem('bag_filter', JSON.stringify(filters));
    this.filtersSubmitted.emit({
      searchText: this.filterForm.controls.searchText.value.trim(),
      filters
    });
  }

  private loadFilterOptions(): void {
    const stagesSub = this._verificationService.getAllStages().subscribe({
      next: (response) => {
        this.stageOptions = response.Data;
        this.restoreSavedFilters();
      }
    });

    const statusSub = this._verificationService
      .getInvoiceFilterStatus('contract')
      .subscribe({
        next: (response) => {
          this.statusOptions = response.Data.filter(
            (status) =>
              !this.statusRemovedFilter.includes(status.Status.toLowerCase().trim())
          ).map((status) => ({
            label: status.Description,
            value: status.Status
          }));

          this.restoreSavedFilters();
        }
      });

    const transferStatusSub = this._verificationService
      .getTransferFilterStatus('transfer')
      .subscribe({
        next: (response) => {
          const transferStatuses = response.Data.BatchStatus
            .map((status) => status.Description)
            .filter(
              (status) =>
                !this.transferStatusRemovedFilter.includes(
                  status.toLowerCase().trim()
                )
            );

          if (!transferStatuses.some((status) => status.toLowerCase().trim() === 'em andamento')) {
            transferStatuses.push('Em Andamento');
          }

          this.transferStatusOptions = transferStatuses;
          this.orderStatusOptions = response.Data.OrderStatus.map(
            (status) => status.Description
          );
          this.restoreSavedFilters();
        }
      });

    this.subs.push(stagesSub, statusSub, transferStatusSub);
  }

  private restoreSavedFilters(): void {
    const savedFilters = this.getSavedFilters();

    this.filterForm.patchValue(
      {
        stages: this.stageOptions.filter((stage) =>
          savedFilters.Stages.includes(stage.toLowerCase())
        ),
        statuses: this.statusOptions
          .filter((status) =>
            (savedFilters.BatchStatuses ?? []).includes(status.value.toLowerCase())
          )
          .map((status) => status.label),
        transferStatuses: this.transferStatusOptions.filter((status) =>
          (savedFilters.StatusOfOnlending ?? []).includes(status.toLowerCase())
        ),
        orderStatuses: this.orderStatusOptions.filter((status) =>
          (savedFilters.OrderStatus ?? []).includes(status.toLowerCase())
        ),
        contractMode: savedFilters.ContractBatchMode ?? (savedFilters.WithoutBatch ? 'withoutBatch' : 'all'),
        transferContractMode:
          savedFilters.ContractBatchMode ?? (savedFilters.WithoutBatch ? 'withoutBatch' : 'all'),
        rateMode: savedFilters.RateMode ?? (savedFilters.IsZeroRate ? 'zeroRate' : 'all'),
        transferRateMode: savedFilters.TransferRateMode ?? 'all',
        context:
          savedFilters.Context ??
          ((savedFilters.StatusOfOnlending?.length ?? 0) > 0 ||
          (savedFilters.OrderStatus?.length ?? 0) > 0
            ? 'transfer'
            : 'invoice')
      },
      { emitEvent: false }
    );
  }

  private getSavedFilters(): IFilterContracts {
    const saved = JSON.parse(localStorage.getItem('bag_filter') ?? '{}');
    return {
      ...this.getDefaultFilters(),
      ...saved
    };
  }

  private getDefaultFilters(): IFilterContracts {
    return {
      Context: 'invoice',
      Stages: [],
      Status: [],
      BatchStatuses: [],
      IsLegacy: false,
      IsZeroRate: false,
      WithoutBatch: false,
      ContractBatchMode: 'all',
      RateMode: 'all',
      TransferRateMode: 'all',
      StatusBank: [],
      StatusOfOnlending: [],
      OrderStatus: [],
      StatusPeriod: []
    };
  }

  private getTriggerText(values: string[], placeholder: string): string {
    if (values.length === 0) {
      return placeholder;
    }

    if (values.length === 1) {
      return values[0];
    }

    return `${values.length} selecionados`;
  }
}
